import React, {useState, useEffect, useRef} from "react";
import TextInput from "../../../components/TextInput";
import {useDispatch, useSelector} from "react-redux";
import {
  getListJobRecommend,
  setIsOpenListJobPanel,
  setJobSelected,
  updateConventionJob,
  setListJobRecommend,
  setCurrentPageLoading,
} from "reducers/chatMessage";
import { bulkMessagesActions } from "reducers/bulkMessages";
import {ThunkDispatch} from "@reduxjs/toolkit";
import CustomIconButton from "../../../components/CustomIconButton";
import {formatPadStartNumber} from "utils/format";
import InfiniteScroll from "react-infinite-scroll-component";
import { LoadingIcon } from "../../../components/Icons";

interface JobPostItemProps {
  item: any;
}
const JobPostItem: React.FC<JobPostItemProps> = ({ item }) => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const { chatSelected, jobSelected } = useSelector((state: any) => state.chatMessage);
  const { needNewFormData } = useSelector((state: any) => state.bulkMessages);

  const handleSelectJob = () => {
    if(!item.is_available) {
      return
    }
    dispatch(setJobSelected(item));
    dispatch(setIsOpenListJobPanel(false));

    // Check convention
    if (chatSelected?.id) {
      dispatch(updateConventionJob({requestBody: {id: chatSelected?.id, job_id: item?.id}}));
    }

    if (needNewFormData && item?.id != jobSelected?.id) {
      dispatch(bulkMessagesActions.setNeedNewFormData(false));
    }
  };

  return (
    <div onClick={handleSelectJob}
         className={`${item.is_available ? 'hover:bg-[#FFF3E5] hover:cursor-pointer' : ''} p-2 flex items-center gap-4 justify-between rounded-[0.25rem] border border-[#E0E0E0]`}>
      <div className="flex-1">
        <p className="text-sm alu-text-base-2">{item.title}</p>
        <p className="text-sm alu-text-base-1">id: {formatPadStartNumber(item.id, 5)}</p>
      </div>
      <div
        className="min-w-[100px] shrink-0 flex items-center gap-1 rounded-[0.25rem] bg-[#F7F7F7] px-2 py-[0.375rem]">
        <span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="13"
            height="13"
            viewBox="0 0 13 13"
            fill="none"
          >
            <circle cx="6.75" cy="6.5" r="6"
                    fill={item.is_available ? '#06C270' : '#BBB'}/>
          </svg>
        </span>
        <p className="text-sm alu-text-base-2">{item.status_str}</p>
      </div>
    </div>
  );
};

const JobPostPanel = ({talentIds=[]}) => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const [inputValue, setInputValue] = useState("");
  const [isReload, setIsReload] = useState(false);
  const [firstFetchingFinish, setFirstFetchingFinish] = useState(false);
  const scrollableDivRef = useRef<HTMLDivElement>(null);
  const { 
    listJobRecommend, talentSelected, currentPageLoading,
    hasMore, pageSizeLoadingJobRec
  } = useSelector((state: any) => state.chatMessage);

  const handeSetSearchQuery = (value: any) => {
    setInputValue(value);
    dispatch(setCurrentPageLoading(1));
    setIsReload(!isReload);
  };

  const handleSetIsOpenListJobPanel = () => {
    dispatch(setCurrentPageLoading(1));
    dispatch(setListJobRecommend([]));
    dispatch(setIsOpenListJobPanel(false));
  };

  const fetchJobRecommend = (page=currentPageLoading) => {
    let dataJob = {}
    if (talentIds.length > 0) {
      dataJob = { search: inputValue, talentIds: talentIds.join(","), pageSize: pageSizeLoadingJobRec, page: page}
    } else {
      dataJob = { search: inputValue, talentId: talentSelected?.talent?.id, pageSize: pageSizeLoadingJobRec, page: page}
    }
    dispatch(getListJobRecommend(dataJob));
  }

  useEffect(() => {
    const page = 1;
    dispatch(setCurrentPageLoading(page));
    fetchJobRecommend(page);
    setFirstFetchingFinish(true);
  }, [isReload]);

  useEffect(() => {
    const checkAndFetch = () => {
      if (hasMore && scrollableDivRef.current && listJobRecommend.length > 0 && firstFetchingFinish) {
        const { scrollHeight, clientHeight } = scrollableDivRef.current;

        if (scrollHeight <= clientHeight) {
          fetchData();
        }
      }
    };

    checkAndFetch();
    
    window.addEventListener("resize", checkAndFetch);

    return () => {
      window.removeEventListener("resize", checkAndFetch);
    };
  }, [listJobRecommend.length, hasMore, firstFetchingFinish]);

  const fetchData = () => {
    setTimeout(() => {
      const page = parseInt(currentPageLoading) + 1;
      dispatch(setCurrentPageLoading(page));
      fetchJobRecommend(page)
    }, 800);
  }

  return (
    <div className="min-w-[392px] max-w-[392px] bg-white rounded-2xl pt-3 alu-panel-box-shadow h-full flex flex-col">
      <div className="flex items-center justify-between px-4 mb-6">
        <h2 className="text-xl leading-[1.875rem] font-bold alu-text-base-2">
          求人情報の選択
        </h2>
        <CustomIconButton onClick={handleSetIsOpenListJobPanel}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <g clipPath="url(#clip0_8574_265)">
              <path
                d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"
                fill="currentColor"
              />
            </g>
            <defs>
              <clipPath id="clip0_8574_265">
                <rect width="24" height="24" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </CustomIconButton>
      </div>
      <div className="relative mb-6 px-4">
        <span className="absolute top-[0.25rem] left-4 bottom-[0.25rem] flex items-center justify-center">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clipPath="url(#clip0_8574_3270)">
              <path
                d="M15.5 14H14.71L14.43 13.73C15.41 12.59 16 11.11 16 9.5C16 5.91 13.09 3 9.5 3C5.91 3 3 5.91 3 9.5C3 13.09 5.91 16 9.5 16C11.11 16 12.59 15.41 13.73 14.43L14 14.71V15.5L19 20.49L20.49 19L15.5 14ZM9.5 14C7.01 14 5 11.99 5 9.5C5 7.01 7.01 5 9.5 5C11.99 5 14 7.01 14 9.5C14 11.99 11.99 14 9.5 14Z"
                fill="#808080"
              />
            </g>
            <defs>
              <clipPath id="clip0_8574_3270">
                <rect width="24" height="24" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </span>
        <TextInput
          id="search"
          name="search"
          value={inputValue}
          onChange={(e: any) => handeSetSearchQuery(e.target.value)}
          placeholder="検索"
          searchIcon={true}
          onClearQuery={() => handeSetSearchQuery("")}
        />
      </div>
      <div 
        id="scrollableDiv"
        ref={scrollableDivRef}
        className="flex-1 px-4 pb-4 flex flex-col gap-1 overflow-y-auto custom-scrollbar"
      >
        <InfiniteScroll
          dataLength={listJobRecommend.length}
          next={fetchData}
          hasMore={hasMore}
          loader={<div className="flex justify-center p-2"><LoadingIcon /></div>}
          scrollableTarget="scrollableDiv"
          scrollThreshold={0.5}
        >
          <div className="flex flex-col gap-1">
            {listJobRecommend?.length > 0 &&
              listJobRecommend.map((job: any, idx: any) => <JobPostItem key={job.id} item={job} />)}
          </div>
        </InfiniteScroll>
      </div>
    </div>
  );
};

export default JobPostPanel;
